import React, { useState } from 'react';
import { BookOpen, Trash2, Pencil, X, Check } from 'lucide-react';
import { useJournal } from '../hooks/useJournal';
import { parseUTCTimestamp } from '../utils/dateUtils';
import { JournalEntrySource } from '../types';
import type { JournalEntry, JournalEntrySource as JournalEntrySourceType } from '../types';

const SOURCE_LABELS: Record<JournalEntrySourceType, string> = {
  free: 'Free Entry',
  daily_start: 'Morning Journal',
  daily_end: 'Evening Reflection',
  daily_general: 'Daily Journal',
  weekly_review: 'Weekly Review',
  monthly_review: 'Monthly Review',
  yearly_review: 'Yearly Review',
};

const SOURCE_COLORS: Record<JournalEntrySourceType, string> = {
  free: 'bg-gray-100 text-gray-700',
  daily_start: 'bg-yellow-100 text-yellow-800',
  daily_end: 'bg-purple-100 text-purple-800',
  daily_general: 'bg-blue-100 text-blue-800',
  weekly_review: 'bg-green-100 text-green-800',
  monthly_review: 'bg-indigo-100 text-indigo-800',
  yearly_review: 'bg-rose-100 text-rose-800',
};

const FILTER_OPTIONS: Array<{ label: string; value: JournalEntrySourceType | 'all' }> = [
  { label: 'All', value: 'all' },
  { label: 'Morning Journal', value: JournalEntrySource.DAILY_START },
  { label: 'Evening Reflection', value: JournalEntrySource.DAILY_END },
  { label: 'Daily Journal', value: JournalEntrySource.DAILY_GENERAL },
  { label: 'Weekly Review', value: JournalEntrySource.WEEKLY_REVIEW },
  { label: 'Monthly Review', value: JournalEntrySource.MONTHLY_REVIEW },
  { label: 'Yearly Review', value: JournalEntrySource.YEARLY_REVIEW },
  { label: 'Free Entry', value: JournalEntrySource.FREE },
];

function formatDateGroup(dateStr: string): string {
  const date = parseUTCTimestamp(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const entryDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (entryDate.getTime() === today.getTime()) return 'Today';
  if (entryDate.getTime() === yesterday.getTime()) return 'Yesterday';
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

function formatTime(dateStr: string): string {
  return parseUTCTimestamp(dateStr).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });
}

function groupByDate(entries: JournalEntry[]): Array<{ date: string; entries: JournalEntry[] }> {
  const groups: Record<string, JournalEntry[]> = {};
  for (const entry of entries) {
    const dateKey = parseUTCTimestamp(entry.created_time).toDateString();
    if (!groups[dateKey]) groups[dateKey] = [];
    groups[dateKey].push(entry);
  }
  return Object.entries(groups).map(([date, items]) => ({
    date,
    entries: items,
  }));
}

export function JournalView() {
  const [sourceFilter, setSourceFilter] = useState<JournalEntrySourceType | 'all'>('all');
  const [newEntryText, setNewEntryText] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState('');

  const filterParams = sourceFilter === 'all' ? {} : { source: sourceFilter };
  const { entries, isLoading, createEntry, updateEntry, deleteEntry, isCreating } =
    useJournal(filterParams);

  const handleCreate = async () => {
    const text = newEntryText.trim();
    if (!text) return;
    await createEntry({ content: text, source: JournalEntrySource.FREE });
    setNewEntryText('');
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleCreate();
    }
  };

  const handleStartEdit = (entry: JournalEntry) => {
    setEditingId(entry.id);
    setEditText(entry.content);
  };

  const handleSaveEdit = async (id: string) => {
    const text = editText.trim();
    if (!text) return;
    await updateEntry(id, { content: text });
    setEditingId(null);
    setEditText('');
  };

  const handleDelete = async (id: string) => {
    await deleteEntry(id);
  };

  const grouped = groupByDate(entries);

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200">Journal</h2>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-3xl mx-auto space-y-6">
          {/* New entry form */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
            <textarea
              value={newEntryText}
              onChange={(e) => setNewEntryText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Write a journal entry..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-gray-400">
                {navigator.platform.includes('Mac') ? 'Cmd' : 'Ctrl'}+Enter to save
              </span>
              <button
                onClick={handleCreate}
                disabled={!newEntryText.trim() || isCreating}
                className="px-4 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                Add Entry
              </button>
            </div>
          </div>

          {/* Source filter pills */}
          <div className="flex flex-wrap gap-2">
            {FILTER_OPTIONS.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setSourceFilter(opt.value)}
                className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                  sourceFilter === opt.value
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Entry feed */}
          {isLoading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p className="text-lg font-medium">No journal entries yet</p>
              <p className="text-sm mt-1">Write your first entry above or complete a review flow</p>
            </div>
          ) : (
            <div className="space-y-6">
              {grouped.map((group) => (
                <div key={group.date}>
                  <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-3 sticky top-0 bg-gray-50 dark:bg-gray-900 py-1">
                    {formatDateGroup(group.entries[0].created_time)}
                  </h3>
                  <div className="space-y-3">
                    {group.entries.map((entry) => (
                      <div
                        key={entry.id}
                        className="group bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            {/* Source badge + period label */}
                            <div className="flex items-center gap-2 mb-2">
                              <span
                                className={`inline-block px-2 py-0.5 text-xs font-medium rounded-full ${
                                  SOURCE_COLORS[entry.source]
                                }`}
                              >
                                {SOURCE_LABELS[entry.source]}
                              </span>
                              {entry.period_label && (
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {entry.period_label}
                                </span>
                              )}
                              {(entry.mood || entry.energy) && (
                                <span className="text-xs text-gray-400">
                                  {entry.mood && `Mood: ${entry.mood}`}
                                  {entry.mood && entry.energy && ' · '}
                                  {entry.energy && `Energy: ${entry.energy}`}
                                </span>
                              )}
                            </div>

                            {/* Prompt */}
                            {entry.prompt && (
                              <p className="text-sm italic text-gray-500 dark:text-gray-400 mb-1">
                                {entry.prompt}
                              </p>
                            )}

                            {/* Content */}
                            {editingId === entry.id ? (
                              <div>
                                <textarea
                                  value={editText}
                                  onChange={(e) => setEditText(e.target.value)}
                                  rows={3}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                                  autoFocus
                                />
                                <div className="flex gap-2 mt-2">
                                  <button
                                    onClick={() => handleSaveEdit(entry.id)}
                                    className="p-1 text-green-600 hover:text-green-700"
                                  >
                                    <Check className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => setEditingId(null)}
                                    className="p-1 text-gray-400 hover:text-gray-600"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <p className="text-gray-800 dark:text-gray-200 whitespace-pre-wrap text-sm">
                                {entry.content}
                              </p>
                            )}
                          </div>

                          {/* Actions + timestamp */}
                          <div className="flex flex-col items-end gap-1 flex-shrink-0">
                            <span className="text-xs text-gray-400">
                              {formatTime(entry.created_time)}
                            </span>
                            {editingId !== entry.id && (
                              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => handleStartEdit(entry)}
                                  className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                                  title="Edit"
                                >
                                  <Pencil className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => handleDelete(entry.id)}
                                  className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                                  title="Delete"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
